/**
 * Delivery Date & Time Picker
 *
 * Business rules (Asia/Dubai, GMT+4):
 *   Order before 16:00  → same day; a window is offered only if it ENDS at
 *                         least SAME_DAY_BUFFER_MIN (2h) after the order time.
 *   Order 16:00 onward  → next day, all windows.
 *
 * Next-day and future dates always get all windows (always >2h away).
 * Operating window closes at 19:00 (last window 5–7 PM).
 */

const DELIVERY_SLOTS = [
  { value: '09:00-11:00', label: '9:00 AM – 11:00 AM' },
  { value: '11:00-13:00', label: '11:00 AM – 1:00 PM' },
  { value: '13:00-15:00', label: '1:00 PM – 3:00 PM' },
  { value: '15:00-17:00', label: '3:00 PM – 5:00 PM' },
  { value: '17:00-19:00', label: '5:00 PM – 7:00 PM' },
];

const SAME_DAY_CUTOFF_HOUR = 16; // 4 PM — last hour same-day orders are accepted
const SAME_DAY_BUFFER_MIN = 120; // 2h — a same-day window must end this long from now

function slotEndMinutes(slot) {
  const [, end] = slot.value.split('-');
  const [h, m] = end.split(':').map(Number);
  return h * 60 + m;
}

function sameDaySlots(dubai) {
  const earliestDeliverable = dubai.hour * 60 + dubai.minute + SAME_DAY_BUFFER_MIN;
  return DELIVERY_SLOTS.filter((s) => slotEndMinutes(s) >= earliestDeliverable);
}

function dubaiNow() {
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Dubai',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  }).formatToParts(now);

  const get = (type) => parts.find((p) => p.type === type).value;
  return {
    year: parseInt(get('year')),
    month: parseInt(get('month')),
    day: parseInt(get('day')),
    hour: parseInt(get('hour')),
    minute: parseInt(get('minute')),
    isoDate: `${get('year')}-${get('month')}-${get('day')}`,
  };
}

function addDays(isoDate, n) {
  const d = new Date(isoDate + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function formatDisplayDate(isoDate) {
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-AE', {
    weekday: 'long', month: 'long', day: 'numeric',
  });
}

function computeEarliest(dubai) {
  const { isoDate } = dubai;
  if (dubai.hour < SAME_DAY_CUTOFF_HOUR) {
    const slots = sameDaySlots(dubai);
    if (slots.length > 0) {
      return { date: isoDate, slot: slots[0].value, allSlots: false };
    }
  }
  return { date: addDays(isoDate, 1), slot: DELIVERY_SLOTS[0].value, allSlots: true };
}

function slotsForDate(isoDate, earliest, dubai) {
  if (isoDate === dubai.isoDate) {
    return dubai.hour < SAME_DAY_CUTOFF_HOUR ? sameDaySlots(dubai) : [];
  }
  return DELIVERY_SLOTS;
}

function buildSlotOptions(slots, selectedSlot, placeholder) {
  const placeholderLabel = placeholder || 'Select delivery time';
  const placeholderOption = `<option value=""${selectedSlot ? '' : ' selected'}>${placeholderLabel}</option>`;
  const options = slots
    .map(
      (s) =>
        `<option value="${s.value}"${s.value === selectedSlot ? ' selected' : ''}>${s.label}</option>`
    )
    .join('');
  return placeholderOption + options;
}

function isBlockedDay(isoDate, blockFriday, blockSaturday) {
  const dow = new Date(isoDate + 'T12:00:00Z').getUTCDay();
  if (blockFriday && dow === 5) return true;
  if (blockSaturday && dow === 6) return true;
  return false;
}

class CartDelivery extends HTMLElement {
  connectedCallback() {
    this._attrDate = this.dataset.attrDate || 'Delivery Date';
    this._attrSlot = this.dataset.attrSlot || 'Delivery Slot';
    this._maxDays = parseInt(this.dataset.maxDays || '14', 10);
    this._blockFriday = this.dataset.blockFriday === 'true';
    this._blockSaturday = this.dataset.blockSaturday === 'true';
    this._sectionId = this.dataset.sectionId;
    this._slotPlaceholder = this.dataset.slotPlaceholder || 'Select delivery time';
    this._errorText = this.dataset.errorText || 'Please select a delivery date and time before checking out.';

    this._dubai = dubaiNow();
    this._earliest = computeEarliest(this._dubai);

    this._savedDate = this.dataset.savedDate || '';
    this._savedSlot = this.dataset.savedSlot || '';

    this._init();
    this._initFlatpickr();
    this._bindSlotChange();
    this._bindCheckoutGuard();
  }

  _init() {
    const slotSelect = this.querySelector('.cart-delivery__slot-select');
    if (!slotSelect) return;
    const slots = this._savedDate
      ? slotsForDate(this._savedDate, this._earliest, this._dubai)
      : [];
    slotSelect.innerHTML = buildSlotOptions(slots, this._savedSlot, this._slotPlaceholder);
    slotSelect.disabled = slots.length === 0;
  }

  _initFlatpickr() {
    const dateInput = this.querySelector('.cart-delivery__date-input');
    const calendarWrap = this.querySelector('.cart-delivery__calendar-wrap');
    if (!dateInput || typeof flatpickr === 'undefined') return;

    const dubai = this._dubai;
    const earliest = this._earliest;
    const blockFriday = this._blockFriday;
    const blockSaturday = this._blockSaturday;
    const maxDate = addDays(dubai.isoDate, this._maxDays);

    this._flatpickr = flatpickr(dateInput, {
      inline: true,
      appendTo: calendarWrap,
      minDate: earliest.date,
      maxDate,
      dateFormat: 'Y-m-d',
      defaultDate: this._savedDate || null,
      disableMobile: true,
      locale: { firstDayOfWeek: 1 },
      disable: [
        (date) => {
          const iso = date.toISOString().slice(0, 10);
          return isBlockedDay(iso, blockFriday, blockSaturday);
        },
      ],
      onChange: (selectedDates, dateStr) => {
        this._onDateChange(dateStr);
      },
    });
  }

  _onDateChange(isoDate) {
    const slotSelect = this.querySelector('.cart-delivery__slot-select');
    const slots = slotsForDate(isoDate, this._earliest, this._dubai);
    if (slotSelect) {
      slotSelect.innerHTML = buildSlotOptions(slots, '', this._slotPlaceholder);
      slotSelect.disabled = slots.length === 0;
    }
    this._selectedDate = isoDate;
    this._selectedSlot = '';
    this._clearError();
  }

  _bindSlotChange() {
    const slotSelect = this.querySelector('.cart-delivery__slot-select');
    if (!slotSelect) return;

    slotSelect.addEventListener('change', () => {
      this._selectedSlot = slotSelect.value;
      if (slotSelect.value) this._clearError();
      this._persistToCart();
    });
  }

  isValid() {
    const dateInput = this.querySelector('.cart-delivery__date-input');
    const slotSelect = this.querySelector('.cart-delivery__slot-select');
    const dateOk = dateInput && dateInput.value.trim() !== '';
    const slotOk = slotSelect && slotSelect.value.trim() !== '';
    return Boolean(dateOk && slotOk);
  }

  _showError() {
    const el = this.querySelector('.cart-delivery__error');
    if (el) {
      el.textContent = this._errorText;
      el.hidden = false;
    }
    this.classList.add('cart-delivery--invalid');
  }

  _clearError() {
    const el = this.querySelector('.cart-delivery__error');
    if (el) el.hidden = true;
    this.classList.remove('cart-delivery--invalid');
  }

  _bindCheckoutGuard() {
    const root = this.closest('cart-drawer') || this.closest('cart-items-component') || document;
    const buttons = root.querySelectorAll('button[name="checkout"]');
    buttons.forEach((btn) => {
      if (btn.__deliveryGuardBound) return;
      btn.__deliveryGuardBound = true;
      btn.addEventListener(
        'click',
        (event) => {
          if (this.isValid()) {
            this._clearError();
            return;
          }
          event.preventDefault();
          event.stopImmediatePropagation();
          this._showError();
          this.scrollIntoView({ behavior: 'smooth', block: 'center' });
        },
        true
      );
    });
  }

  get _debouncedSave() {
    if (!this.__debouncedSave) {
      this.__debouncedSave = theme.utils.debounce(() => this._persistToCart(), 50);
    }
    return this.__debouncedSave;
  }

  _persistToCart() {
    const date = this._selectedDate || this._savedDate;
    const slotSelect = this.querySelector('.cart-delivery__slot-select');
    const slot =
      (slotSelect && slotSelect.value) || this._selectedSlot || this._savedSlot;

    if (!date || !slot) return;

    const slotObj = DELIVERY_SLOTS.find((s) => s.value === slot);
    const slotLabel = slotObj ? slotObj.label : slot;

    fetch(routes.cart_update_url, {
      ...fetchConfig('json'),
      body: JSON.stringify({
        attributes: {
          [this._attrDate]: formatDisplayDate(date),
          [this._attrSlot]: slotLabel,
          ['_' + this._attrDate]: date,
          ['_' + this._attrSlot]: slot,
        },
      }),
    })
      .catch((err) => console.error('[CartDelivery] save failed', err));
  }
}

if (!customElements.get('cart-delivery')) {
  customElements.define('cart-delivery', CartDelivery);
}
